import { type IReactionDisposer, makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { CountryEnum, PhoneInputStore, toastStore } from 'shared/ui'
import { logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { type IResponseCallForwarding, type IResponseNumber } from 'entities/Phone/api/types'
import { PhoneApi } from 'entities/Phone'
import { SettingsRoutes } from 'entities/ExportRouters'
import { TextToSpeechWidgetStore } from 'widgets/TextToSpeech/store/textToSpeechWidgetStore'
import { CallForwardingModalContent } from 'pages/settings/pages/inboxes/pages/id/pages/numbers/ui/CallForwardingModal/CallForwardingModalContent'
import { type NumbersStore } from 'pages/settings/pages/inboxes/pages/id/store/numbersStore'

export class CallForwardingStore {
  private _modalCallForwardingId = nanoid()
  private _callForwardingEnabled = false
  private _callForwardingExtension = ''
  private _callWhisperEnabled = false
  private _callScreeningEnabled = false
  private _callWhisperMessage = ''
  private _loadingSaveCallForwarding = false
  private _textToSpeechStore: TextToSpeechWidgetStore | null = null
  private _disposeWhisperMessage: IReactionDisposer | null = null
  private _originalCallForwarding: IResponseCallForwarding | null = null
  phoneInputStore = new PhoneInputStore()

  constructor(private readonly numbersStore: NumbersStore) {
    makeAutoObservable(this)
  }

  get callForwardingEnabled() {
    return this._callForwardingEnabled
  }

  get number() {
    return this.phoneInputStore.number
  }

  get fullNumber() {
    return this.phoneInputStore.fullNumber
  }

  get formattedNumber() {
    let formattedNumber = this.number

    if (this.number.length && this.phoneInputStore.country.id === CountryEnum.GB) {
      formattedNumber = `+44${this.number.replace(/[^0-9]/g, '')}`
    }

    if (this.number.length && this.phoneInputStore.country.id === CountryEnum.AU) {
      formattedNumber = `+61${this.number.replace(/[^0-9]/g, '')}`
    }

    return formattedNumber
  }

  get callForwardingExtension() {
    return this._callForwardingExtension
  }

  get callWhisperEnabled() {
    return this._callWhisperEnabled
  }

  get callScreeningEnabled() {
    return this._callScreeningEnabled
  }

  get callWhisperMessage() {
    return this._callWhisperMessage
  }

  get textToSpeechStore() {
    return this._textToSpeechStore
  }

  get loadingSaveCallForwarding() {
    return this._loadingSaveCallForwarding
  }

  get isCallForwardingSaveDisabled() {
    if (!this._callForwardingEnabled) {
      if (this._originalCallForwarding?.is_active) {
        return false
      }
      return true
    }

    if (!this.number.trim()) {
      return true
    }

    if (!this._originalCallForwarding) {
      return false
    }

    const original = this._originalCallForwarding
    const originalCallWhisper = original.call_whisper || ''
    const currentCallWhisper = this._textToSpeechStore?.text || ''

    const hasChanges =
      original.is_active !== this._callForwardingEnabled ||
      (original.number || '') !== this.fullNumber ||
      (original.extension || '') !== this._callForwardingExtension ||
      original.is_call_whisper !== this._callWhisperEnabled ||
      original.is_screening !== this._callScreeningEnabled ||
      originalCallWhisper !== currentCallWhisper

    return !hasChanges
  }

  dispose = () => {
    this._disposeWhisperMessage?.()
    this._textToSpeechStore = null
  }

  closeCallForwardingModal = () => {
    runInAction(() => {
      this._callForwardingEnabled = false
      this._callForwardingExtension = ''
      this._callWhisperEnabled = false
      this._callScreeningEnabled = false
      this._callWhisperMessage = ''
      this._textToSpeechStore = null
      this._originalCallForwarding = null
      this.phoneInputStore.reset()
    })
    modalStore.removeModal(this._modalCallForwardingId)
  }

  toggleCallForwarding = (value: boolean) => {
    this._callForwardingEnabled = value
    if (!value) {
      this.phoneInputStore.setNumberError('')
    }
  }

  setCallForwardingExtension = (value: string) => {
    const valueExtension = value.replace(/[^0-9]/g, '')
    this._callForwardingExtension = valueExtension
  }

  toggleCallWhisper = (value: boolean) => {
    this._callWhisperEnabled = value
    if (!value) {
      this.textToSpeechStore?.clearError()
    }
    if (value && !this._textToSpeechStore) {
      this.initializeTextToSpeechStore()
    }
  }

  initializeTextToSpeechStore = () => {
    this._textToSpeechStore = new TextToSpeechWidgetStore(
      {
        type: 'create',
        onClose: () => {},
        onCreateSpeech: () => {},
        group: 'voicemail',
      },
      this.numbersStore.entities
    )

    this._textToSpeechStore.setIsHideVoicesDropdown(true)

    if (this._callWhisperMessage) {
      this._textToSpeechStore.setMessageData({
        message: this._callWhisperMessage,
        isFocus: false,
        isReplace: true,
        attachments: [],
      })
    }

    this._disposeWhisperMessage?.()
  }

  toggleCallScreening = (value: boolean) => {
    this._callScreeningEnabled = value
  }

  openWithoutAutoRechargeModal = async () => {
    modalStore.addModal({
      id: this._modalCallForwardingId,
      type: ModalTypeList.ALERT,
      title: 'Auto Recharge Required',
      desc: 'To use call forwarding, your organization must have auto recharge enabled.',
      primaryAction: {
        onAction: async () => {
          modalStore.closeModal(this._modalCallForwardingId)

          uiStore.changeRouteViaVue({
            path: `${SettingsRoutes.autoRecharge}`,
          })
        },
        text: 'Enable auto-recharge',
      },
      showCloseButton: false,
    })
  }

  openCallForwardingModal = async (number: IResponseNumber) => {
    const isAutoRecharge = this.numbersStore.entities.billingStore.autorecharge?.is_active

    if (!isAutoRecharge) {
      return this.openWithoutAutoRechargeModal()
    }

    runInAction(() => {
      const cf = number.call_forwarding
      const fixedNumber =
        cf?.country === CountryEnum.CA || cf?.country === CountryEnum.US
          ? cf?.national_number
          : cf?.number?.slice(3)

      this._callForwardingEnabled = cf?.is_active || false
      this.phoneInputStore.setNumber(fixedNumber || '')
      this.phoneInputStore.setCountryById(number.call_forwarding?.country ?? CountryEnum.US)
      this._callForwardingExtension = cf?.extension || ''
      this._callWhisperEnabled = cf?.is_call_whisper || false
      this._callScreeningEnabled = cf?.is_screening || false
      this._callWhisperMessage = cf?.call_whisper || ''
      this.phoneInputStore.setNumberError('')

      this._originalCallForwarding = cf || null

      if (this._callWhisperEnabled) {
        this.initializeTextToSpeechStore()
      }
    })

    modalStore.addModal({
      id: this._modalCallForwardingId,
      title: number.call_forwarding?.is_active
        ? 'Edit'
        : `Call forwarding for ${number.formatted_number}`,
      width: 480,
      fullHeight: true,
      showCloseButton: false,
      paddingContent: '0 24px 24px',
      onClose: this.closeCallForwardingModal,
      ModalContentProps: {
        store: this,
        number,
      },
      ModalContent: CallForwardingModalContent,
    })
  }

  validadeCallForwarding = (callWhisperMessage: string) => {
    if (this._callForwardingEnabled && this._callWhisperEnabled && !callWhisperMessage.trim()) {
      this._textToSpeechStore?.setValidError('A short message should not be empty')
      return false
    }

    return true
  }

  saveCallForwarding = async (numberId: number) => {
    const callWhisperMessage = this._textToSpeechStore?.text || ''
    const isValid = this.validadeCallForwarding(callWhisperMessage)

    if (!isValid) return

    try {
      runInAction(() => {
        this._loadingSaveCallForwarding = true
      })

      await PhoneApi.updateCallForwarding(numberId, {
        is_active: this._callForwardingEnabled,
        number: this.formattedNumber,
        extension: this._callForwardingExtension,
        is_call_whisper: this._callWhisperEnabled,
        call_whisper: callWhisperMessage,
        is_screening: this._callScreeningEnabled,
      })

      toastStore.add({
        type: 'success',
        title: this._callForwardingEnabled ? 'Forwarding active' : 'Forwarding disabled',
      })

      this.closeCallForwardingModal()
      if (this.numbersStore.currentInboxId) {
        await this.numbersStore.init(this.numbersStore.currentInboxId)
      }
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        if (e.errors.getError('number')) {
          this.phoneInputStore.setNumberError(e.errors.getError('number'))
        } else {
          toastStore.add({
            type: 'error',
            title:
              e.errors.getError('message') ||
              e.errors.getError('default') ||
              'Failed to update call forwarding',
          })
        }
      }
    } finally {
      runInAction(() => {
        this._loadingSaveCallForwarding = false
      })
    }
  }
}
